import { useEffect } from 'react';
import { RotateCcw } from 'lucide-react';
import useAnalyticsStore from '../stores/analyticsStore';

// Filter controls for the analytics event timeline ([6.3]). Dropdown options
// are facets pulled from /events/aggregate (distinct groupKey per dimension).
// Each change calls store.setFilter, which resets paging and refetches the
// first page. cameraId is shown as its raw id for now (camera-name resolution
// arrives in a later slice).

const AnalyticsFilterPanel = () => {
  const filters = useAnalyticsStore((s) => s.filters);
  const facets = useAnalyticsStore((s) => s.facets);
  const setFilter = useAnalyticsStore((s) => s.setFilter);
  const resetFilters = useAnalyticsStore((s) => s.resetFilters);
  const fetchFacets = useAnalyticsStore((s) => s.fetchFacets);
  const setDefaultWindow = useAnalyticsStore((s) => s.setDefaultWindow);
  const setAllTime = useAnalyticsStore((s) => s.setAllTime);
  const cameraLabel = useAnalyticsStore((s) => s.cameraLabel);
  // [8.3b] camera options come from the devices map (no event scan), sorted by
  // label. Subscribing to cameraNames also re-renders when it loads.
  const cameraNames = useAnalyticsStore((s) => s.cameraNames);
  const cameraOptions = Object.keys(cameraNames).sort((a, b) =>
    (cameraNames[a] || a).localeCompare(cameraNames[b] || b)
  );
  // embedded camera-scoped mode hides the Camera control (already pinned)
  const lockedCameraId = useAnalyticsStore((s) => s.lockedCameraId);

  useEffect(() => {
    fetchFacets();
  }, [fetchFacets]);

  // Active time-window indicator ([8.3a]). The default is a rolling 30d window;
  // unbounded ("All time") is opt-in. A custom range (manual inputs or a
  // histogram bar-click) reads as "Custom range".
  const isAllTime = !filters.from && !filters.to;
  let isDefault = false;
  if (filters.from && filters.to) {
    const span = (new Date(filters.to).getTime() - new Date(filters.from).getTime()) / 86400000;
    isDefault = Math.abs(span - 30) < 1;
  }
  const windowLabel = isAllTime ? 'All time' : isDefault ? 'Last 30 days' : 'Custom range';

  return (
    <div className="analytics-filters">
      <div className="analytics-window">
        <span className="analytics-window-label">Window: {windowLabel}</span>
        <div className="analytics-window-toggle">
          <button
            type="button"
            className={'analytics-window-btn' + (isDefault ? ' active' : '')}
            onClick={setDefaultWindow}
          >
            Last 30 days
          </button>
          <button
            type="button"
            className={'analytics-window-btn' + (isAllTime ? ' active' : '')}
            onClick={setAllTime}
            title="Query all events (slower at scale)"
          >
            All time
          </button>
        </div>
      </div>

      <div className="analytics-filter">
        <label>Category</label>
        <select
          value={filters.eventCategory}
          onChange={(e) => setFilter('eventCategory', e.target.value)}
        >
          <option value="">All categories</option>
          {facets.categories.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
      </div>

      <div className="analytics-filter">
        <label>Vendor</label>
        <select
          value={filters.vendor}
          onChange={(e) => setFilter('vendor', e.target.value)}
        >
          <option value="">All vendors</option>
          {facets.vendors.map((v) => (
            <option key={v} value={v}>{v}</option>
          ))}
        </select>
      </div>

      <div className="analytics-filter">
        <label>Topic</label>
        <select
          value={filters.canonicalTopic}
          onChange={(e) => setFilter('canonicalTopic', e.target.value)}
        >
          <option value="">All topics</option>
          {facets.topics.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
      </div>

      {!lockedCameraId && (
        <div className="analytics-filter">
          <label>Camera</label>
          <select
            value={filters.cameraId}
            onChange={(e) => setFilter('cameraId', e.target.value)}
          >
            <option value="">All cameras</option>
            {cameraOptions.map((c) => (
              <option key={c} value={c}>{cameraLabel(c)}</option>
            ))}
          </select>
        </div>
      )}

      <div className="analytics-filter">
        <label>From</label>
        <input
          type="datetime-local"
          value={filters.from}
          onChange={(e) => setFilter('from', e.target.value)}
        />
      </div>

      <div className="analytics-filter">
        <label>To</label>
        <input
          type="datetime-local"
          value={filters.to}
          onChange={(e) => setFilter('to', e.target.value)}
        />
      </div>

      <button type="button" className="analytics-filter-reset" onClick={resetFilters}>
        <RotateCcw size={14} />
        <span>Reset</span>
      </button>
    </div>
  );
};

export default AnalyticsFilterPanel;
